import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

const LoginForm = ({ onForgotPassword, setToken, darkMode }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });

      const { token } = response.data;
      
      // Save token to local storage or context
      localStorage.setItem('token', token);
      if (setToken) {
        setToken(token);
      }
      
      // Redirect to dashboard or home
      navigate('/dashboard');
      toast.success('Login successful!');
      
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-6"
    >
      <div className="flex flex-col items-center justify-center gap-3">
        <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          🔒
        </div>
        <h1 className="text-xl font-extrabold text-center">
          Welcome Back
        </h1>
        <p className="text-sm">Sign in to manage your portfolio</p>
      </div>

      {error && (
        <div className="bg-red-200/20 border border-red-500 text-red-900 px-4 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-2 space-y-6">
        <div className="space-y-4">
          <div className="relative">
            <label htmlFor="email" className="sr-only">Email address</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`block w-full pl-10 pr-3 py-3 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                placeholder="Email address"
              />
            </div>
          </div>

          <div className="relative">
            <label htmlFor="password" className="sr-only">Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faLock} className="text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className={`block w-full pl-10 pr-3 py-3 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                placeholder="Password"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onForgotPassword && onForgotPassword();
            }}
            className="text-sm font-medium text-blue-600 hover:text-blue-300 cursor-pointer"
          >
            Forgot your password?
          </button>
        </div>

        <div>
          <motion.button
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-3 px-4 text-sm font-semibold rounded-md text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

LoginForm.propTypes = {
  onForgotPassword: PropTypes.func.isRequired,
  setToken: PropTypes.func,
  darkMode: PropTypes.bool
};

export default LoginForm;
